import React, { useState, useEffect } from 'react'
import { X, Clock } from 'lucide-react'

const EditShiftModal = ({ isOpen, onClose, onSubmit, shift }) => {
  const [formData, setFormData] = useState({
    shiftName: '',
    shiftCode: '',
    breakHours: '',
    workingHours: '',
    startTime: '',
    endTime: '',
    notes: '',
    status: 'active'
  })
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})

  // Populate form when shift data changes
  useEffect(() => {
    if (shift && isOpen) {
      setFormData({
        shiftName: shift.shiftName || '',
        shiftCode: shift.shiftCode || '',
        breakHours: shift.breakHours || '',
        workingHours: shift.workingHours || '',
        startTime: shift.startTime || '',
        endTime: shift.endTime || '',
        notes: shift.notes || '',
        status: shift.status || 'active'
      })
      setErrors({})
    }
  }, [shift, isOpen])

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.shiftName.trim()) {
      newErrors.shiftName = 'Shift name is required'
    }

    if (!formData.shiftCode.trim()) {
      newErrors.shiftCode = 'Shift code is required'
    }

    if (!formData.breakHours || formData.breakHours < 0) {
      newErrors.breakHours = 'Break hours must be 0 or greater'
    }

    if (!formData.workingHours || formData.workingHours < 1) {
      newErrors.workingHours = 'Working hours must be at least 1'
    }

    if (!formData.startTime) {
      newErrors.startTime = 'Start time is required'
    }

    if (!formData.endTime) {
      newErrors.endTime = 'End time is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    try {
      setLoading(true)
      
      // Convert values to appropriate types
      const submitData = {
        ...formData,
        breakHours: parseInt(formData.breakHours),
        workingHours: parseInt(formData.workingHours)
      }

      await onSubmit(submitData)
    } catch (error) {
      console.error('Error submitting form:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setErrors({})
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 ">
          <div className="flex items-center space-x-3">
            {/* <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
              <Clock className="h-4 w-4 text-orange-600" />
            </div> */}
            <h2 className="text-xl font-semibold text-gray-900">Edit Shift</h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Shift Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Shift Name *
              </label>
              <input
                type="text"
                value={formData.shiftName}
                onChange={(e) => handleInputChange('shiftName', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                  errors.shiftName ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="e.g., Morning Shift"
                required
              />
              {errors.shiftName && (
                <p className="text-red-500 text-sm mt-1">{errors.shiftName}</p>
              )}
            </div>

            {/* Shift Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Shift Code *
              </label>
              <input
                type="text"
                value={formData.shiftCode}
                onChange={(e) => handleInputChange('shiftCode', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                  errors.shiftCode ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="e.g., MS1"
                required
              />
              {errors.shiftCode && (
                <p className="text-red-500 text-sm mt-1">{errors.shiftCode}</p>
              )}
            </div>

            {/* Break Hours */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Break Hours *
              </label>
              <input
                type="number"
                min="0"
                value={formData.breakHours}
                onChange={(e) => handleInputChange('breakHours', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                  errors.breakHours ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="e.g., 1"
                required
              />
              {errors.breakHours && (
                <p className="text-red-500 text-sm mt-1">{errors.breakHours}</p>
              )}
            </div>

            {/* Working Hours */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Working Hours *
              </label>
              <input
                type="number"
                min="1"
                value={formData.workingHours}
                onChange={(e) => handleInputChange('workingHours', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                  errors.workingHours ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="e.g., 9"
                required
              />
              {errors.workingHours && (
                <p className="text-red-500 text-sm mt-1">{errors.workingHours}</p>
              )}
            </div>

            {/* Start Time */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Time *
              </label>
              <input
                type="time"
                value={formData.startTime}
                onChange={(e) => handleInputChange('startTime', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                  errors.startTime ? 'border-red-500' : 'border-gray-300'
                }`}
                required
              />
              {errors.startTime && (
                <p className="text-red-500 text-sm mt-1">{errors.startTime}</p>
              )}
            </div>

            {/* End Time */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Time *
              </label>
              <input
                type="time"
                value={formData.endTime}
                onChange={(e) => handleInputChange('endTime', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                  errors.endTime ? 'border-red-500' : 'border-gray-300'
                }`}
                required
              />
              {errors.endTime && (
                <p className="text-red-500 text-sm mt-1">{errors.endTime}</p>
              )}
            </div>
          </div>

          {/* Status */}
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">
              Status
            </label>
            <button
              type="button"
              onClick={() => handleInputChange('status', formData.status === 'active' ? 'inactive' : 'active')}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-orange-600 focus:ring-offset-2 ${
                formData.status === 'active' ? 'bg-orange-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  formData.status === 'active' ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              placeholder="Additional notes about this shift..."
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-4 pt-6 border-t">
            <button
              type="button"
              onClick={handleClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : 'Save Shift'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default EditShiftModal
